import { useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';

const HERO_IMG = 'https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?w=900&q=85&auto=format&fit=crop';

function MaskedLine({ children, delay = 0, className = '' }) {
  return (
    <div style={{ overflow: 'hidden' }}>
      <motion.div
        initial={{ y: '110%' }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay }}
        className={className}
      >
        {children}
      </motion.div>
    </div>
  );
}

export default function HeroSection({ onFilterChange, galleryFilter }) {
  const cardRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rawRX = useTransform(my, [-120, 120], [10, -10]);
  const rawRY = useTransform(mx, [-120, 120], [-10, 10]);
  const rotateX = useSpring(rawRX, { stiffness: 100, damping: 22 });
  const rotateY = useSpring(rawRY, { stiffness: 100, damping: 22 });

  const handleMouseMove = (e) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set(e.clientX - (rect.left + rect.width / 2));
    my.set(e.clientY - (rect.top + rect.height / 2));
  };
  const handleMouseLeave = () => { mx.set(0); my.set(0); };

  const filters = ['All', 'Editorial', 'Architecture', 'Portraits'];

  const handleFilterChip = (f) => {
    onFilterChange(f);
    document.getElementById('works')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen bg-[#FDFCF7] pt-28 pb-16 flex items-center" id="hero">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-12 lg:gap-20 items-center">

          {/* — Left Column — */}
          <div>
            {/* Eyebrow */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="text-[11px] uppercase font-mono tracking-[0.28em] text-neutral-400">
                Editorial &amp; Architectural Photography
              </span>
              <span className="w-8 h-px bg-neutral-300" />
              <span className="text-[11px] font-mono text-[#B88349] tracking-widest">EST. 2015</span>
            </motion.div>

            {/* Main Headline — Masked reveal */}
            <div data-testid="hero-heading" className="mb-6">
              <div className="text-[clamp(3.2rem,8.5vw,7.5rem)] font-serif font-light text-[#121212] leading-[0.95]">
                <MaskedLine delay={0.3}>Silent</MaskedLine>
              </div>
              <div className="text-[clamp(3.2rem,8.5vw,7.5rem)] font-serif font-light italic text-[#121212] leading-[0.95]">
                <MaskedLine delay={0.48}>Frames.</MaskedLine>
              </div>
            </div>

            {/* Subtitle */}
            <div style={{ overflow: 'hidden' }} className="mb-10">
              <motion.p
                data-testid="hero-subtext"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1], delay: 0.7 }}
                className="text-base sm:text-lg text-neutral-500 font-light leading-relaxed max-w-md"
              >
                The poetry of architecture, human stillness,
                and transient light — captured through the
                geometry of patience.
              </motion.p>
            </div>

            {/* CTA Row */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.95 }}
              className="flex flex-wrap items-center gap-5 mb-12"
            >
              <button
                data-testid="hero-explore-btn"
                onClick={() => document.getElementById('works')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#121212] text-white rounded-full px-8 py-3.5 text-sm font-medium tracking-wide hover:bg-neutral-700 active:scale-[0.98] transition-all duration-300"
              >
                Explore Works
              </button>
              <span className="text-[11px] font-mono text-neutral-400 tracking-[0.18em] uppercase">
                48.8566° N, 2.3522° E
              </span>
            </motion.div>

            {/* Filter Chips */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1.15 }}
              className="flex flex-wrap gap-2"
            >
              {filters.map((f) => (
                <button
                  key={f}
                  onClick={() => handleFilterChip(f)}
                  className={`rounded-full px-4 py-1.5 text-[11px] font-mono uppercase tracking-widest border transition-all duration-300 ${
                    galleryFilter === f
                      ? 'bg-[#121212] text-white border-[#121212]'
                      : 'border-neutral-300 text-neutral-500 hover:border-neutral-600 hover:text-[#121212]'
                  }`}
                >
                  {f}
                </button>
              ))}
            </motion.div>
          </div>

          {/* — Right Column: Parallax Card — */}
          <motion.div
            ref={cardRef}
            data-testid="hero-parallax-card"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.55 }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            className="relative hidden lg:block"
            style={{ perspective: 1200 }}
          >
            <motion.div
              style={{ rotateX, rotateY }}
              className="relative rounded-2xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.18)] cursor-none"
            >
              <img
                src={HERO_IMG}
                alt="Aperture & Monolith, Valencia 2025"
                className="w-full h-[580px] object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              {/* EXIF */}
              <div className="absolute bottom-5 left-5">
                <p className="text-[10px] font-mono tracking-widest uppercase text-white/50 mb-0.5">Hasselblad 907X</p>
                <p className="text-xs font-mono tracking-wide text-white/85">45mm • f/4.0 • 1/500s</p>
              </div>
              {/* Corner marks */}
              {['top-4 left-4 border-t border-l', 'top-4 right-4 border-t border-r', 'bottom-4 left-4 border-b border-l', 'bottom-4 right-4 border-b border-r'].map((c, i) => (
                <div key={i} className={`absolute w-5 h-5 border-white/30 ${c}`} />
              ))}
            </motion.div>

            {/* Floating exhibition badge */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 1.4 }}
              className="absolute -top-5 -right-5 bg-white rounded-xl p-3.5 shadow-lg border border-neutral-100"
            >
              <p className="text-[10px] font-mono text-neutral-400 uppercase tracking-widest mb-1">Exhibitions</p>
              <p className="text-xs font-medium text-[#121212]">Arles '25 · Tokyo Photo</p>
            </motion.div>

            {/* Location tag */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 1.6 }}
              className="absolute -bottom-5 -left-5 bg-[#FDFCF7] rounded-xl p-3.5 shadow-md border border-neutral-100"
            >
              <p className="text-[10px] font-mono text-neutral-400">Valencia, Spain</p>
              <p className="text-[11px] font-mono text-[#B88349] tracking-wide font-medium">2025</p>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom Meta Strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.6 }}
          className="mt-16 pt-6 border-t border-black/[0.06] flex flex-wrap items-center justify-between gap-6"
        >
          <div className="flex flex-wrap items-center gap-8 sm:gap-12">
            {[
              { label: 'Based in', value: 'Paris · Tokyo' },
              { label: 'Available', value: 'Q3 / Q4 2025', accent: true },
              { label: 'Clients', value: 'Hermès · Vogue · AD' },
            ].map(({ label, value, accent }) => (
              <div key={label}>
                <p className="text-[10px] font-mono text-neutral-400 uppercase tracking-widest mb-0.5">{label}</p>
                <p className={`text-sm font-light ${accent ? 'text-[#B88349]' : 'text-[#121212]'}`}>{value}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-col items-center gap-1">
            <span className="text-[10px] font-mono text-neutral-400 tracking-widest uppercase">Scroll</span>
            <motion.div
              animate={{ scaleY: [0.3, 1, 0.3] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
              className="w-px h-8 bg-neutral-300"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
